// MA-03b — Private-beta access gate screen.
//
// Shown to signed-in users whose email is NOT on the access_allowlist.
// No child PII is collected or displayed. A sign-out CTA lets the user
// try a different account. Matches LoginScreen's design-token language
// (cream/terracotta/Fraunces) so both doors read as one product.
//
// MA-935 (D-TUX-23): the screen picks its own variant, so App.jsx (which
// passes only `email` and `onSignOut`) is not edited:
//   - confirm: this tab holds a valid pending claim code (pendingClaimCode.ts)
//     and the account has an email. "Use this account" calls
//     admit_by_claim_code once (claimCodeAdmission.ts), then reloads so the
//     gate re-checks. A PGRST202 answer (the function is not applied yet) or
//     any other error falls back to the text below; it never admits.
//   - refused: the server answered 'refused'.
//   - teacher: the device role (pw_user_role) is teacher, or (MA-1056) the
//     visitor came through the website's /teacher/start entry
//     (pendingTeacherStart.ts) — a teacher blocked by the beta gate sees the
//     teacher text, never a blank or an error.
//   - default: today's text.
// "Try a different account" signs out, which clears the pending code
// (supabaseClient.js signOut), as today. A class join code
// (pendingJoinCode.ts) is never read here: it admits no one.
// Nothing on this screen is spoken.

import { useRef, useState } from "react";
import { Pal } from "@/components/Pal";
import { readPendingClaimCode } from "@/lib/teacher/pendingClaimCode";
import { admitByClaimCode, type AdmitOutcome } from "@/lib/teacher/claimCodeAdmission";
import { getUserRole, type UserRole } from "@/lib/userRole";
import { readPendingTeacherStart } from "@/lib/teacher/pendingTeacherStart";

export const REQUEST_ACCESS_HREF = "mailto:support@pencilwise.app?subject=Beta%20access%20request";
export const TEACHER_REQUEST_ACCESS_HREF = "mailto:support@pencilwise.app?subject=Teacher%20beta%20access%20request";

export type PrivateBetaVariant = "confirm" | "refused" | "teacher" | "default";

interface Props {
  email?: string;
  onSignOut: () => void;
  /** Test seams (MA-935). App.jsx passes none of these; the defaults are
   *  the real tab-scoped code, the real device role, the real RPC and a
   *  real page reload. */
  pendingClaimCode?: string | null;
  role?: UserRole;
  admit?: (code: string) => Promise<AdmitOutcome>;
  reload?: () => void;
  /** MA-1056 test seam: a pending /teacher/start entry. Default: the real one. */
  teacherStart?: boolean;
}

type Phase = "idle" | "busy" | "refused" | "fallback";

function defaultReload(): void {
  if (typeof window !== "undefined") window.location.reload();
}

/** The variant ladder, in order. Exported for the rendered tests. */
export function pickPrivateBetaVariant(args: {
  pendingClaimCode: string | null;
  email?: string;
  role: UserRole;
  phase: Phase;
  teacherStart?: boolean;
}): PrivateBetaVariant {
  if (args.phase === "refused") return "refused";
  if (args.phase !== "fallback" && args.pendingClaimCode && args.email) return "confirm";
  if (args.role === "teacher" || args.teacherStart === true) return "teacher";
  return "default";
}

const PRIMARY =
  "mt-8 inline-flex min-h-[60px] w-full max-w-[340px] cursor-pointer items-center justify-center gap-2 rounded-3xl bg-primary px-6 py-4 text-lg font-extrabold text-primary-foreground shadow-press no-underline transition-transform hover:-translate-y-0.5 active:translate-y-0.5 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-ring disabled:cursor-default disabled:opacity-60";
const LINK =
  "mt-2 cursor-pointer appearance-none border-0 bg-transparent text-sm font-semibold text-muted-foreground underline hover:text-foreground focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-ring";

export default function PrivateBetaScreen({ email, onSignOut, pendingClaimCode, role, admit, reload, teacherStart }: Props) {
  const [code] = useState<string | null>(() => (pendingClaimCode !== undefined ? pendingClaimCode : readPendingClaimCode()));
  const [deviceRole] = useState<UserRole>(() => role ?? getUserRole());
  const [fromTeacherStart] = useState<boolean>(() => teacherStart ?? readPendingTeacherStart() !== null);
  const [phase, setPhase] = useState<Phase>("idle");
  const inFlight = useRef(false);

  const variant = pickPrivateBetaVariant({ pendingClaimCode: code, email, role: deviceRole, phase, teacherStart: fromTeacherStart });

  async function confirmAccount() {
    if (inFlight.current || !code) return;
    inFlight.current = true;
    setPhase("busy");
    const outcome = await (admit ?? ((c: string) => admitByClaimCode(c)))(code);
    if (outcome === "admitted" || outcome === "already") {
      (reload ?? defaultReload)();
      return;
    }
    setPhase(outcome === "refused" ? "refused" : "fallback");
  }

  const heading = variant === "teacher" ? "PencilWise is in a private beta for teachers." : "PencilWise is in private beta.";

  return (
    <div
      data-testid="private-beta"
      data-variant={variant}
      className="flex min-h-dvh flex-col items-center justify-center bg-background px-6 py-10 text-center font-sans text-foreground"
    >
      <p className="m-0 font-serif text-lg font-bold tracking-tight">
        Pencil<span className="text-primary">Wise</span>
      </p>

      <div className="relative mt-6 grid place-items-center">
        <div className="absolute h-44 w-44 rounded-full bg-gradient-to-b from-pal-blue/25 to-cream-deep/50" />
        <Pal color="blue" mood="wave" size={150} className="relative" />
      </div>

      <h1 className="mx-auto mt-6 max-w-[22ch] font-serif text-[28px] font-bold leading-[1.15] tracking-tight sm:text-[36px]">
        {heading}
      </h1>

      {variant === "confirm" ? (
        <>
          <p data-testid="private-beta-message" className="mx-auto mt-3 max-w-[320px] text-base font-semibold leading-relaxed text-muted-foreground [overflow-wrap:anywhere]">
            You&apos;re signed in as {email}. Use your child&apos;s code with this account?
          </p>
          <button
            type="button"
            data-testid="private-beta-use-account"
            onClick={confirmAccount}
            disabled={phase === "busy"}
            className={PRIMARY}
          >
            Use this account
          </button>
          <button type="button" data-testid="private-beta-sign-out" onClick={onSignOut} className={`${LINK} mt-4`}>
            Try a different account
          </button>
        </>
      ) : (
        <>
          <p data-testid="private-beta-message" className="mx-auto mt-3 max-w-[320px] text-base font-semibold leading-relaxed text-muted-foreground">
            {variant === "refused" ? (
              <>This code can&apos;t let you in. It may have expired or already been used.</>
            ) : (
              <>
                Your spot isn&apos;t confirmed yet. Request access and we&apos;ll be in
                touch when it&apos;s ready.
              </>
            )}
          </p>

          <a
            data-testid="private-beta-request-access"
            href={variant === "teacher" ? TEACHER_REQUEST_ACCESS_HREF : REQUEST_ACCESS_HREF}
            className={PRIMARY}
          >
            Request access
          </a>

          <p className="mt-4 text-sm font-semibold text-muted-foreground">
            Signed in as {email ?? "unknown"}
          </p>

          <button type="button" data-testid="private-beta-sign-out" onClick={onSignOut} className={LINK}>
            Try a different account
          </button>
        </>
      )}
    </div>
  );
}
